import React, { useCallback, useMemo, useState } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE, OWNER_AVATAR_SIZES } from '@/shared/lib/constants';
import { EMPTY_MESSAGES } from '@/shared/lib/constants/messages';
import { formatDateTime } from '@/shared/lib/format';
import { openTagInNewTab } from '@/shared/lib/navigation';
import { mapTagsToBasicInfo } from '@/shared/lib/tag';
import { highlightText } from '@/shared/lib/text/highlight';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import type { TagSource } from '@/shared/types/openmetadata';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { DrawerDomainList } from '@/shared/ui/AssetMetadata';
import { BadgeList } from '@/shared/ui/BadgeList';
import { ColumnVisibilityButton } from '@/shared/ui/ColumnVisibilityButton/ColumnVisibilityButton';
import { useColumnVisibility } from '@/shared/ui/ColumnVisibilityButton/useColumnVisibility';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { OwnerAvatars } from '@/shared/ui/OwnerAvatars';
import { SearchInput } from '@/shared/ui/SearchInput';
import { Switch } from '@/shared/ui/Switch';
import { TableEmpty } from '@/shared/ui/TableEmpty';
import { Tooltip } from '@/shared/ui/Tooltip';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { filterEntityLabels } from '@/entities/search';

import type { DatabaseSchema } from '@/features/asset-detail/api/databaseSchemasApi';
import { useSchemaTablesCount } from '@/features/asset-detail/model';

import { SEARCH_PLACEHOLDERS } from '@/widgets/asset-detail/shared/constants';

import styles from './SchemasTab.module.css';

type SchemasTabProps = {
  schemas: DatabaseSchema[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  onDeletedToggle: (includeDeleted: boolean) => void;
  includeDeleted: boolean;
};

type ColumnKey =
  | 'name'
  | 'description'
  | 'tableCount'
  | 'owners'
  | 'domains'
  | 'tags'
  | 'glossaryTerms'
  | 'usage'
  | 'updatedAt';

type BadgeItem = {
  tagFQN: string;
  name: string;
  displayName: string;
};

const COLUMN_WIDTHS = {
  NAME: 200,
  DESCRIPTION: 352,
  TABLE_COUNT: 120,
  DOMAINS: 352,
  TAGS: 352,
  GLOSSARY_TERMS: 352,
  USAGE: 150,
  UPDATED_AT: 160,
  OWNERS: 352,
} as const;

const createBadgeItems = (tags: DatabaseSchema['tags'], source: TagSource): BadgeItem[] => {
  const filteredTags = filterEntityLabels(tags, {
    includeOnlySource: source,
  });
  if (filteredTags.length === 0) return [];

  return mapTagsToBasicInfo(filteredTags);
};

const SchemasTabComponent: React.FC<SchemasTabProps> = ({
  schemas,
  isLoading = false,
  isError = false,
  error,
  onRetry,
  onDeletedToggle,
  includeDeleted,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);

  const schemaFqns = useMemo(() => schemas.map((schema) => schema.fullyQualifiedName), [schemas]);
  const { countsMap, errorsMap } = useSchemaTablesCount(schemaFqns);

  const DEFAULT_COLUMN_ORDER = useMemo<ColumnKey[]>(() => {
    const columns: ColumnKey[] = ['name', 'description', 'tableCount'];
    if (showOwners) columns.push('owners');
    columns.push('domains', 'tags', 'glossaryTerms', 'usage', 'updatedAt');
    return columns;
  }, [showOwners]);

  const DEFAULT_VISIBLE_COLUMNS = useMemo<ColumnKey[]>(() => {
    return DEFAULT_COLUMN_ORDER;
  }, [DEFAULT_COLUMN_ORDER]);

  const filteredSchemas = useMemo(() => {
    if (!searchQuery || searchQuery.length === 0) return schemas;

    const lowerQuery = searchQuery.toLowerCase();
    return schemas.filter((schema) => {
      const nameMatch = schema.name.toLowerCase().includes(lowerQuery);
      const fqnMatch = schema.fullyQualifiedName.toLowerCase().includes(lowerQuery);
      const descriptionMatch = schema.description?.toLowerCase().includes(lowerQuery);

      const classificationTags = filterEntityLabels(schema.tags, {
        includeOnlySource: 'Classification',
        requireConfirmed: true,
      });
      const glossaryTerms = filterEntityLabels(schema.tags, {
        includeOnlySource: 'Glossary',
        requireConfirmed: true,
      });

      const tagMatch = classificationTags?.some((tag) => tag.tagFQN?.toLowerCase().includes(lowerQuery));
      const termMatch = glossaryTerms?.some((term) => term.tagFQN?.toLowerCase().includes(lowerQuery));

      return nameMatch || fqnMatch || descriptionMatch || tagMatch || termMatch;
    });
  }, [schemas, searchQuery]);

  const hasData = filteredSchemas.length > 0;

  const allColumnsMap: Record<ColumnKey, ColumnsType<DatabaseSchema>[number]> = useMemo(() => {
    const columns: Record<string, ColumnsType<DatabaseSchema>[number]> = {
      name: {
        title: '이름',
        dataIndex: 'name',
        key: 'name',
        width: COLUMN_WIDTHS.NAME,
        fixed: 'left',
        render: (name: string, record: DatabaseSchema) => (
          <Link to={`/databaseSchema/${record.fullyQualifiedName}`} className={styles.schemaName}>
            {searchQuery ? highlightText(name, searchQuery, { className: 'highlight' }) : name}
          </Link>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      description: {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        width: hasData ? COLUMN_WIDTHS.DESCRIPTION : 'auto',
        render: (description?: string) => {
          return (
            <AssetDescription
              description={description}
              mode="expandable"
              highlightQuery={searchQuery}
              highlightClassName="highlight"
            />
          );
        },
      },
      tableCount: {
        title: '테이블',
        dataIndex: 'tableCount',
        key: 'tableCount',
        width: COLUMN_WIDTHS.TABLE_COUNT,
        align: 'center',
        render: (_: unknown, record: DatabaseSchema) => {
          const fqn = record.fullyQualifiedName;
          const hasError = errorsMap.get(fqn);
          const count = countsMap.get(fqn);

          if (hasError) {
            return (
              <Tooltip title="테이블 개수를 불러올 수 없습니다">
                <ExclamationCircleOutlined style={{ color: 'var(--opds-glo-palette-red-50)', fontSize: '14px' }} />
              </Tooltip>
            );
          }

          return count !== undefined ? count.toLocaleString() : '-';
        },
        sorter: (a, b) => {
          const aCount = countsMap.get(a.fullyQualifiedName) || 0;
          const bCount = countsMap.get(b.fullyQualifiedName) || 0;
          return aCount - bCount;
        },
      },
      domains: {
        title: '도메인',
        dataIndex: 'domains',
        key: 'domains',
        width: COLUMN_WIDTHS.DOMAINS,
        render: (domains: DatabaseSchema['domains']) => {
          if (!domains || domains.length === 0) return '-';
          return <DrawerDomainList domains={domains} />;
        },
      },
      tags: {
        title: '태그',
        dataIndex: 'tags',
        key: 'tags',
        width: COLUMN_WIDTHS.TAGS,
        render: (tags: DatabaseSchema['tags']) => {
          const badgeItems = createBadgeItems(tags, 'Classification');
          if (badgeItems.length === 0) return '-';
          return <BadgeList isEllipsis={false} items={badgeItems} onItemClick={openTagInNewTab} maxVisibleCount={5} />;
        },
      },
      glossaryTerms: {
        title: '용어',
        dataIndex: 'tags',
        key: 'glossaryTerms',
        width: COLUMN_WIDTHS.GLOSSARY_TERMS,
        render: (tags: DatabaseSchema['tags']) => {
          const badgeItems = createBadgeItems(tags, 'Glossary');
          if (badgeItems.length === 0) return '-';
          return (
            <BadgeList
              items={badgeItems}
              onItemClick={(fqn) => {
                const url = buildAssetDetailUrl('glossaryTerm', fqn);
                navigate(url);
              }}
              iconName="book"
              maxVisibleCount={5}
            />
          );
        },
      },
      usage: {
        title: '사용량',
        dataIndex: 'usageSummary',
        key: 'usage',
        width: COLUMN_WIDTHS.USAGE,
        render: (usageSummary?: DatabaseSchema['usageSummary']) => {
          if (!usageSummary) return '-';
          const { weeklyStats } = usageSummary;
          return `${weeklyStats.count} (${weeklyStats.percentileRank}%)`;
        },
        sorter: (a, b) => {
          const aCount = a.usageSummary?.weeklyStats.count || 0;
          const bCount = b.usageSummary?.weeklyStats.count || 0;
          return aCount - bCount;
        },
      },
      updatedAt: {
        title: '업데이트 날짜',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: COLUMN_WIDTHS.UPDATED_AT,
        render: (updatedAt?: number) => {
          if (!updatedAt) return '-';
          return formatDateTime(updatedAt);
        },
        sorter: (a, b) => (a.updatedAt || 0) - (b.updatedAt || 0),
      },
    };

    if (showOwners) {
      columns.owners = {
        title: '소유자',
        dataIndex: 'owners',
        key: 'owners',
        width: COLUMN_WIDTHS.OWNERS,
        render: (owners: DatabaseSchema['owners']) => {
          if (!owners || owners.length === 0) return '-';
          return <OwnerAvatars owners={owners} size={OWNER_AVATAR_SIZES.TABLE} />;
        },
      };
    }

    return columns as Record<ColumnKey, ColumnsType<DatabaseSchema>[number]>;
  }, [hasData, showOwners, errorsMap, countsMap, navigate, searchQuery]);

  const allColumnsMapForVisibility = useMemo(
    () =>
      Object.entries(allColumnsMap).reduce(
        (acc, [key, col]) => ({
          ...acc,
          [key]: { title: col.title as string },
        }),
        {} as Record<ColumnKey, { title: string }>
      ),
    [allColumnsMap]
  );

  const { state } = useColumnVisibility<ColumnKey>({
    defaultColumnOrder: DEFAULT_COLUMN_ORDER,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    allColumnsMap: allColumnsMapForVisibility,
  });

  const { columnOrder, visibleColumns } = state;

  const visibleColumnsData = useMemo<ColumnsType<DatabaseSchema>>(() => {
    return columnOrder
      .filter((key) => Object.hasOwn(allColumnsMap, key) && visibleColumns.includes(key))
      .map((key) => allColumnsMap[key]);
  }, [columnOrder, visibleColumns, allColumnsMap]);

  const pageParam = searchParams.get('page');
  const pageSizeParam = searchParams.get('pageSize');
  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  const initialPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : DEFAULT_PAGE_SIZE;

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', String(page));
      newSearchParams.set('pageSize', String(pageSize));
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const { paginationConfig, scrollConfig } = usePagination({
    dataLength: filteredSchemas.length,
    viewportOffset: 648,
    initialPage,
    initialPageSize,
    onPageChange: handlePageChange,
  });

  if (isError) {
    return (
      <ErrorFallback
        error={error}
        description="스키마 정보를 불러오지 못했습니다. 다시 시도해 주세요."
        resetErrorBoundary={onRetry}
      />
    );
  }

  return (
    <div className={styles.schemasTab}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder={SEARCH_PLACEHOLDERS.SCHEMAS}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className={styles.toolbarRight}>
          <div className={styles.deletedToggle}>
            <Switch checked={includeDeleted} onChange={onDeletedToggle} aria-label="삭제됨 포함" color="standard" />
            <span className={styles.toggleLabel}>삭제됨</span>
          </div>
          <ColumnVisibilityButton
            defaultColumnOrder={DEFAULT_COLUMN_ORDER}
            defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
            allColumnsMap={allColumnsMapForVisibility}
          />
        </div>
      </div>
      <Table
        columns={visibleColumnsData}
        dataSource={visibleColumnsData.length === 0 ? [] : filteredSchemas}
        rowKey={(record) => record.id}
        loading={isLoading}
        pagination={paginationConfig}
        scroll={{ x: filteredSchemas.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
        locale={{
          emptyText: (
            <TableEmpty
              searchQuery={searchQuery}
              isVisibleColumns={visibleColumnsData.length === 0}
              description={searchQuery ? undefined : EMPTY_MESSAGES.NO_TABLE_ITEMS}
            />
          ),
        }}
        className={styles.schemasTable}
      />
    </div>
  );
};

export const SchemasTab = React.memo(SchemasTabComponent);
